import type { ApiResponse } from "@playarena/shared";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getEnv } from "@/utils/helpers";

interface PaymentIntent {
  clientSecret: string;
  amount: number;
  currency: string;
}

interface PayPalOrder {
  id: string;
  status: string;
  links: Array<{
    href: string;
    rel: string;
    method: string;
  }>;
}

export const paymentApi = createApi({
  reducerPath: "paymentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${getEnv()?.VITE_API_URL}/${getEnv()?.VITE_APP_VERSION}/payment`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Payment"],
  endpoints: builder => ({
    createStripePaymentIntent: builder.mutation<
      ApiResponse<PaymentIntent>,
      { amount: number; currency?: string }
    >({
      query: data => ({
        url: "/stripe/create-intent",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Store payment intent in local state
            dispatch({ type: "payment/setPaymentIntent", payload: data.data });
          }
        } catch (error) {
          console.error("paymentApi.createStripePaymentIntent error:", error);
        }
      },
    }),

    confirmStripePayment: builder.mutation<
      ApiResponse<any>,
      { paymentIntentId: string; paymentMethodId: string }
    >({
      query: data => ({
        url: "/stripe/confirm",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Clear payment intent and update payment status
            dispatch({ type: "payment/clearPaymentIntent" });
            dispatch({ type: "payment/setPaymentStatus", payload: "confirmed" });
          }
        } catch (error) {
          console.error("paymentApi.confirmStripePayment error:", error);
          // Handle payment confirmation error
          dispatch({ type: "payment/setPaymentStatus", payload: "failed" });
        }
      },
      invalidatesTags: ["Payment"],
    }),

    createPayPalOrder: builder.mutation<
      ApiResponse<PayPalOrder>,
      { amount: number; currency?: string }
    >({
      query: data => ({
        url: "/paypal/create-order",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Store PayPal order in local state
            dispatch({ type: "payment/setPaypalOrder", payload: data.data });
          }
        } catch (error) {
          console.error("paymentApi.createPayPalOrder error:", error);
        }
      },
    }),

    capturePayPalOrder: builder.mutation<ApiResponse<any>, string>({
      query: orderId => ({
        url: `/paypal/capture/${orderId}`,
        method: "POST",
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Clear PayPal order and update payment status
            dispatch({ type: "payment/clearPaypalOrder" });
            dispatch({ type: "payment/setPaymentStatus", payload: "captured" });
          }
        } catch (error) {
          console.error("paymentApi.capturePayPalOrder error:", error);
          // Handle PayPal order capture error
          dispatch({ type: "payment/setPaymentStatus", payload: "failed" });
        }
      },
      invalidatesTags: ["Payment"],
    }),

    processRefund: builder.mutation<ApiResponse<any>, { bookingId: string; amount?: number }>({
      query: data => ({
        url: "/refund",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update refund status
            dispatch({ type: "payment/setRefundStatus", payload: "processed" });
          }
        } catch (error) {
          console.error("paymentApi.processRefund error:", error);
          // Handle refund processing error
          dispatch({ type: "payment/setRefundStatus", payload: "failed" });
        }
      },
      invalidatesTags: ["Payment"],
    }),

    getPaymentHistory: builder.query<ApiResponse<any[]>, string>({
      query: userId => `/history/${userId}`,
      providesTags: ["Payment"],
    }),

    getPaymentMethods: builder.query<ApiResponse<any[]>, void>({
      query: () => "/methods",
      providesTags: ["Payment"],
    }),
  }),
});

export const {
  useCreateStripePaymentIntentMutation,
  useConfirmStripePaymentMutation,
  useCreatePayPalOrderMutation,
  useCapturePayPalOrderMutation,
  useProcessRefundMutation,
  useGetPaymentHistoryQuery,
  useGetPaymentMethodsQuery,
} = paymentApi;
